import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  Typography,
  Paper,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Divider,
  MenuItem
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Header from './Header';
import { useLanguage } from '../context/LanguageContext';
import LanguageModal from './LanguageModal';

const VolunteerRegistration = ({ userEmail, onBack, onProfileClick }) => {
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const { t, currentLanguage, getLanguageName, isRtl } = useLanguage();
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    volunteerTypes: [],
    firstName: '',
    lastName: '',
    email: userEmail || '',
    phone: '',
    birthday: '',
    address: {
      street: '',
      city: '',
      state: '',
      zip: ''
    },
    emergencyContact: {
      name: '',
      relationship: '',
      phone: ''
    },
    education: '',
    employment: {
      isEmployed: '',
      employer: '',
      employerPhone: '',
      position: ''
    },
    skills: [],
    otherSkills: '',
    church: '',
    spiritualBeliefs: '',
    substanceUse: '',
    criminalHistory: '',
    criminalExplanation: '',
    references: [
      { name: '', email: '', relationship: '' },
      { name: '', email: '', relationship: '' }
    ],
    backgroundCheckConsent: false
  });

  // Skill options
  const skillOptions = [
    'Computer', 'Administration', 'Teaching', 'Leadership',
    'Prayer', 'Social', 'Communication', 'Photography'
  ];

  // Education options
  const educationOptions = [
    'Middle School',
    'High School/GED',
    'Associate Degree',
    'Undergraduate Degree',
    'Graduate Degree',
    'Professional Licenses / Specialized Training'
  ];

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNestedChange = (parentField, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [field]: value
      }
    }));
  };

  const handleReferenceChange = (index, field, value) => {
    const updatedReferences = [...formData.references];
    updatedReferences[index][field] = value;
    setFormData(prev => ({
      ...prev,
      references: updatedReferences
    }));
  };

  const handleVolunteerTypeChange = (type) => {
    setFormData(prev => {
      const types = [...prev.volunteerTypes];
      const index = types.indexOf(type);

      if (index === -1) {
        types.push(type);
      } else {
        types.splice(index, 1);
      }

      return { ...prev, volunteerTypes: types };
    });
  };

  const handleSkillChange = (skill) => {
    setFormData(prev => {
      const skills = [...prev.skills];
      const index = skills.indexOf(skill);

      if (index === -1) {
        skills.push(skill);
      } else {
        skills.splice(index, 1);
      }

      return { ...prev, skills };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await fetch("https://me40bh7o7b.execute-api.us-east-1.amazonaws.com/prod/volunteer", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          user_email: userEmail,
          formData
        })
      });

      if (!response.ok) throw new Error("Submission failed");

      alert('Thank you for your volunteer application!');
      onBack();
    } catch (err) {
      console.error("Submission error:", err);
      alert('There was an error submitting your application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#F5F5F5', direction: isRtl ? 'rtl' : 'ltr' }}>
      <Header
        showProfileIcon={true}
        onProfileClick={onProfileClick}
        onLanguageClick={() => setShowLanguageModal(true)}
        languageLabel={getLanguageName(currentLanguage)}
      />

      <LanguageModal
        open={showLanguageModal}
        onClose={() => setShowLanguageModal(false)}
      />

      <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
        <Card sx={{
          width: '100%',
          maxWidth: 800,
          height: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: 2,
          boxShadow: 3,
          position: 'relative'
        }}>
          <IconButton
            sx={{
              position: 'absolute',
              left: isRtl ? 8 : 'auto',
              right: isRtl ? 'auto' : 8,
              top: 8,
              zIndex: 2,
              color: '#fff'
            }}
            onClick={onBack}
          >
            <CloseIcon />
          </IconButton>

          <CardHeader
            title={<Typography variant="h6" sx={{ color: '#fff' }}>Volunteer Registration</Typography>}
            sx={{
              backgroundColor: '#E27A58',
              position: 'relative'
            }}
          />

          <CardContent sx={{
            flexGrow: 1,
            overflowY: 'auto',
            px: 3,
          }}>
            <form onSubmit={handleSubmit}>
              {/* Volunteer Type Selection */}
              <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid rgba(0, 0, 0, 0.12)' }}>
                <Typography variant="subtitle1" gutterBottom>
                  Are you interested in serving as a: (select one or both)
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.volunteerTypes.includes('Reading Circle')}
                        onChange={() => handleVolunteerTypeChange('Reading Circle')}
                      />
                    }
                    label="Reading Circle Mentor"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.volunteerTypes.includes('Preschool Circle')}
                        onChange={() => handleVolunteerTypeChange('Preschool Circle')}
                      />
                    }
                    label="Preschool Circle Volunteer"
                  />
                </FormGroup>
              </Paper>

              {/* Basic Information */}
              <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid rgba(0, 0, 0, 0.12)' }}>
                <Typography variant="subtitle1" gutterBottom>Basic Information</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Birthday"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      name="birthday"
                      value={formData.birthday}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Address */}
              <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid rgba(0, 0, 0, 0.12)' }}>
                <Typography variant="subtitle1" gutterBottom>Address</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Street Address"
                      name="street"
                      value={formData.address.street}
                      onChange={(e) => handleNestedChange('address', 'street', e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="City"
                      name="city"
                      value={formData.address.city}
                      onChange={(e) => handleNestedChange('address', 'city', e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="State"
                      name="state"
                      value={formData.address.state}
                      onChange={(e) => handleNestedChange('address', 'state', e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="ZIP Code"
                      name="zip"
                      value={formData.address.zip}
                      onChange={(e) => handleNestedChange('address', 'zip', e.target.value)}
                      required
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Emergency Contact */}
              <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid rgba(0, 0, 0, 0.12)' }}>
                <Typography variant="subtitle1" gutterBottom>Emergency Contact</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      name="name"
                      value={formData.emergencyContact.name}
                      onChange={(e) => handleNestedChange('emergencyContact', 'name', e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Relationship"
                      name="relationship"
                      value={formData.emergencyContact.relationship}
                      onChange={(e) => handleNestedChange('emergencyContact', 'relationship', e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone"
                      name="phone"
                      value={formData.emergencyContact.phone}
                      onChange={(e) => handleNestedChange('emergencyContact', 'phone', e.target.value)}
                      required
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Education */}
              <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid rgba(0, 0, 0, 0.12)' }}>
                <Typography variant="subtitle1" gutterBottom>Education</Typography>
                <TextField
                  select
                  fullWidth
                  label="Highest Level of Education"
                  name="education"
                  value={formData.education}
                  onChange={handleChange}
                  required
                >
                  {educationOptions.map(option => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </TextField>
              </Paper>

              {/* Employment */}
              <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid rgba(0, 0, 0, 0.12)' }}>
                <Typography variant="subtitle1" gutterBottom>Employment</Typography>
                <Typography variant="body2" gutterBottom>Are you currently employed?</Typography>
                <RadioGroup
                  name="isEmployed"
                  value={formData.employment.isEmployed}
                  onChange={(e) => handleNestedChange('employment', 'isEmployed', e.target.value)}
                  row
                  required
                >
                  <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="No" control={<Radio />} label="No" />
                </RadioGroup>

                {formData.employment.isEmployed === 'Yes' && (
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Employer"
                        name="employer"
                        value={formData.employment.employer}
                        onChange={(e) => handleNestedChange('employment', 'employer', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Employer Phone"
                        name="employerPhone"
                        value={formData.employment.employerPhone}
                        onChange={(e) => handleNestedChange('employment', 'employerPhone', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Position and Length of Employment"
                        name="position"
                        value={formData.employment.position}
                        onChange={(e) => handleNestedChange('employment', 'position', e.target.value)}
                        multiline
                        rows={2}
                      />
                    </Grid>
                  </Grid>
                )}
              </Paper>

              {/* More About You */}
              <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid rgba(0, 0, 0, 0.12)' }}>
                <Typography variant="subtitle1" gutterBottom>More About You</Typography>

                <Typography variant="body2" gutterBottom>Please list your skills, gifts and interests:</Typography>
                <FormGroup row>
                  {skillOptions.map(skill => (
                    <FormControlLabel
                      key={skill}
                      control={
                        <Checkbox
                          checked={formData.skills.includes(skill)}
                          onChange={() => handleSkillChange(skill)}
                        />
                      }
                      label={skill}
                    />
                  ))}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.skills.includes('Other')}
                        onChange={() => {
                          handleSkillChange('Other');
                          if (!formData.skills.includes('Other')) {
                            setFormData(prev => ({ ...prev, otherSkills: '' }));
                          }
                        }}
                      />
                    }
                    label="Other"
                  />
                </FormGroup>

                {formData.skills.includes('Other') && (
                  <TextField
                    fullWidth
                    label="Other skills/interests"
                    name="otherSkills"
                    value={formData.otherSkills}
                    onChange={handleChange}
                    sx={{ mt: 1 }}
                    required={formData.skills.includes('Other')}
                  />
                )}

                <TextField
                  fullWidth
                  label="Church Attendance"
                  name="church"
                  value={formData.church}
                  onChange={handleChange}
                  sx={{ mt: 2 }}
                  helperText="Do you attend a church? If so, where do you worship?"
                  FormHelperTextProps={{
                    sx: {
                      whiteSpace: 'normal',
                      wordWrap: 'break-word'
                    }
                  }}
                />

                <TextField
                  fullWidth
                  label="Spiritual Beliefs"
                  name="spiritualBeliefs"
                  value={formData.spiritualBeliefs}
                  onChange={handleChange}
                  multiline
                  rows={4}
                  required
                  sx={{ mt: 2 }}
                  helperText="Discuss your spiritual beliefs. Do you feel like you have a relationship with God? What does the gospel mean to you?"
                  FormHelperTextProps={{
                    sx: {
                      whiteSpace: 'normal',
                      wordWrap: 'break-word'
                    }
                  }}
                />

                <Typography variant="body2" gutterBottom sx={{ mt: 2 }}>
                  Are you presently abusing alcohol or using illegal drugs?
                </Typography>
                <RadioGroup
                  name="substanceUse"
                  value={formData.substanceUse}
                  onChange={handleChange}
                  row
                  required
                >
                  <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="No" control={<Radio />} label="No" />
                </RadioGroup>

                <Typography variant="body2" gutterBottom sx={{ mt: 2 }}>
                  Have you ever been convicted of, pleaded guilty or no contest to, placed on probation, given community supervision, or given deferred adjudication for a crime or are you now under charges for any criminal offense?
                </Typography>
                <RadioGroup
                  name="criminalHistory"
                  value={formData.criminalHistory}
                  onChange={handleChange}
                  row
                  required
                >
                  <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="No" control={<Radio />} label="No" />
                </RadioGroup>

                {(formData.substanceUse === 'Yes' || formData.criminalHistory === 'Yes') && (
                  <TextField
                    fullWidth
                    label="Additional Explanation"
                    name="criminalExplanation"
                    value={formData.criminalExplanation}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    required
                    sx={{ mt: 2 }}
                    helperText="Answering yes won't automatically disqualify you. Please explain your situation."
                    FormHelperTextProps={{
                      sx: {
                        whiteSpace: 'normal',
                        wordWrap: 'break-word'
                      }
                    }}
                  />
                )}
              </Paper>

              {/* References */}
              <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid rgba(0, 0, 0, 0.12)' }}>
                <Typography variant="subtitle1" gutterBottom>Character References</Typography>
                <Typography variant="body2" gutterBottom>
                  Please list two character references (not relatives):
                </Typography>

                {formData.references.map((ref, index) => (
                  <Box key={index} sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Reference {index + 1}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Name"
                          value={ref.name}
                          onChange={(e) => handleReferenceChange(index, 'name', e.target.value)}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Email"
                          type="email"
                          value={ref.email}
                          onChange={(e) => handleReferenceChange(index, 'email', e.target.value)}
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Relationship"
                          value={ref.relationship}
                          onChange={(e) => handleReferenceChange(index, 'relationship', e.target.value)}
                          required
                        />
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Paper>

              {/* Background Check Consent */}
              <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid rgba(0, 0, 0, 0.12)' }}>
                <Typography variant="subtitle1" gutterBottom>Background Check Consent</Typography>
                <Typography variant="body2" gutterBottom>
                  A background check/screening will be required of all Refugee Resources, Inc. volunteers prior to participation in any RRI sponsored program.
                  Purpose: To ensure the safety and well being of participants in the Refugee Resources, Inc. program through the Reading Circle and Preschool Circle,
                  individuals with regular, repetitive access or contact with children in Refugee Resources, Inc. must complete the screening process.
                  We believe that volunteers are crucial to the delivery of the Reading Circle and Preschool Circle, and we desire to provide a safe environment for our participants.
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.backgroundCheckConsent}
                      onChange={(e) => setFormData(prev => ({ ...prev, backgroundCheckConsent: e.target.checked }))}
                      required
                    />
                  }
                  label="By submitting this form, you give us permission to run a background check."
                />
              </Paper>

              {/* Submit Button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={submitting}
                  sx={{ bgcolor: '#E27A58', '&:hover': { bgcolor: '#d2694a' } }}
                >
                  {submitting ? 'Submitting...' : 'Submit Application'}
                </Button>
              </Box>
            </form>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ p: 2, bgcolor: '#1EAEB8', color: '#fff', textAlign: 'center', mt: 'auto' }}>
        <Typography variant="body2">© {new Date().getFullYear()} Refugee Resources, Inc.</Typography>
      </Box>
    </Box>
  );
};

export default VolunteerRegistration;
